import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Download, KeyRound, Ban, CheckCircle, Pencil, Users, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export default function UsersTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");

  const { data: users = [] } = useQuery({
    queryKey: ["admin-users", q],
    queryFn: async () => (await api.get("/admin/users", { params: { q } })).data,
  });

  const exportCsv = async () => {
    const res = await api.get("/admin/users/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "utenti_fantasyrace.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Elenco utenti esportato");
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Nuova password per ${u.username} (min 6 caratteri):`);
    if (!pw) return;
    try {
      await api.post(`/admin/users/${u.id}/reset-password`, { password: pw });
      toast.success(`Password di ${u.username} aggiornata`);
    } catch (e) {
      toast.error(errMsg(e, "Reset fallito"));
    }
  };

  const toggleStatus = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/toggle-status`);
      toast.success(`${u.username}: account ${data.status}`);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) {
      toast.error(errMsg(e, "Operazione fallita"));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Eliminare DEFINITIVAMENTE ${u.username}? Verranno rimossi anche squadra, messaggi wall e appartenenze alle leghe. Operazione irreversibile.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.username} eliminato definitivamente (cascade)`);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-leagues"] });
    } catch (e) {
      toast.error(errMsg(e, "Eliminazione fallita"));
    }
  };

  const renameUser = async (u) => {    const username = window.prompt("Nuovo username (invariato se confermi):", u.username);
    if (username === null) return;
    const team_name = window.prompt("Nuovo nome scuderia (invariato se confermi):", u.team_name || "");
    if (team_name === null) return;
    const payload = {};
    if (username.trim() && username.trim() !== u.username) payload.username = username.trim();
    if (team_name.trim() && team_name.trim() !== u.team_name) payload.team_name = team_name.trim();
    if (!Object.keys(payload).length) return;
    try {
      await api.put(`/admin/users/${u.id}/rename`, payload);
      toast.success("Utente aggiornato d'ufficio");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) {
      toast.error(errMsg(e, "Modifica fallita"));
    }
  };

  const { data: leagues = [] } = useQuery({
    queryKey: ["admin-leagues"],
    queryFn: async () => (await api.get("/admin/leagues")).data,
  });

  const renameLeague = async (l) => {
    const name = window.prompt("Nuovo nome per la lega:", l.name);
    if (!name || !name.trim() || name.trim() === l.name) return;
    try {
      await api.put(`/admin/leagues/${l.id}`, { name: name.trim() });
      toast.success("Lega rinominata");
      qc.invalidateQueries({ queryKey: ["admin-leagues"] });
    } catch (e) {
      toast.error(errMsg(e, "Operazione fallita"));
    }
  };

  const limitLeague = async (l) => {
    const v = window.prompt("Limite massimo partecipanti:", String(l.max_members ?? 20));
    if (!v) return;
    try {
      await api.put(`/admin/leagues/${l.id}`, { max_members: parseInt(v) });
      toast.success("Limite aggiornato");
      qc.invalidateQueries({ queryKey: ["admin-leagues"] });
    } catch (e) {
      toast.error(errMsg(e, "Operazione fallita"));
    }
  };

  const deleteLeague = async (l) => {
    if (!window.confirm(`Eliminare definitivamente la lega "${l.name}"?`)) return;
    await api.delete(`/admin/leagues/${l.id}`);
    toast.success("Lega eliminata");
    qc.invalidateQueries({ queryKey: ["admin-leagues"] });
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="users-search-input"
            className="rounded-lg bg-[#121214] border border-[#3F3F4C] pl-9 pr-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport w-64"
            placeholder="Cerca username o email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <button
          data-testid="users-export-button"
          onClick={exportCsv}
          className="flex items-center gap-2 rounded-lg border border-[#FFB703] text-[#FFB703] hover:bg-[#FFB703]/10 transition-colors px-4 py-2 font-body-sport font-bold text-xs tracking-widest uppercase"
        >
          <Download className="w-4 h-4" /> Esporta CSV
        </button>
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-[#3F3F4C] bg-[#1A1A1E]">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b border-[#3F3F4C] bg-[#121214]">
              {["Username", "Email", "Iscrizione", "Squadra", "Punti", "Stato", "Azioni"].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.id} className={`border-b border-[#242429] ${i % 2 === 0 ? "bg-[#1A1A1E]" : "bg-[#141416]"}`}>
                <td className="px-4 py-2.5 font-body-sport font-semibold text-white text-sm">{u.username}</td>
                <td className="px-4 py-2.5 text-xs text-gray-400">{u.email}</td>
                <td className="px-4 py-2.5 font-time text-[11px] text-gray-500">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-300">{u.team_name || "—"}</td>
                <td className="px-4 py-2.5 font-time font-bold text-[#FFB703] text-sm">{u.total_points}</td>
                <td className="px-4 py-2.5">
                  <span
                    data-testid={`user-status-${u.id}`}
                    className={`font-body-sport text-[10px] font-bold tracking-widest px-2 py-0.5 rounded border ${
                      u.status === "sospeso"
                        ? "text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10"
                        : "text-[#22C55E] border-[#22C55E]/40 bg-[#22C55E]/10"
                    }`}
                  >
                    {u.status === "sospeso" ? "SOSPESO" : "ATTIVO"}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1.5">
                    <button
                      data-testid={`user-rename-${u.id}`}
                      onClick={() => renameUser(u)}
                      title="Modifica username / scuderia d'ufficio"
                      className="p-1.5 rounded border border-[#3F3F4C] text-gray-400 hover:text-white hover:border-white transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      data-testid={`user-reset-${u.id}`}
                      onClick={() => resetPassword(u)}
                      title="Resetta password"
                      className="p-1.5 rounded border border-[#3F3F4C] text-gray-400 hover:text-[#FFB703] hover:border-[#FFB703] transition-colors"
                    >
                      <KeyRound className="w-3.5 h-3.5" />
                    </button>
                    <button
                      data-testid={`user-toggle-${u.id}`}
                      onClick={() => toggleStatus(u)}
                      title={u.status === "sospeso" ? "Riattiva" : "Sospendi"}
                      className={`p-1.5 rounded border transition-colors ${
                        u.status === "sospeso"
                          ? "border-[#3F3F4C] text-gray-400 hover:text-[#22C55E] hover:border-[#22C55E]"
                          : "border-[#3F3F4C] text-gray-400 hover:text-[#FF1E27] hover:border-[#FF1E27]"
                      }`}
                    >
                      {u.status === "sospeso" ? <CheckCircle className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      data-testid={`user-delete-${u.id}`}
                      onClick={() => deleteUser(u)}
                      title="Elimina definitivamente (squadra, leghe, messaggi)"
                      className="p-1.5 rounded border border-[#3F3F4C] text-gray-400 hover:text-[#EF4444] hover:border-[#EF4444] transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 pt-4">
        <h3 className="font-racing font-bold text-sm uppercase tracking-wide text-white border-l-4 border-[#FFB703] pl-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-[#FFB703]" /> Moderazione Leghe Private
        </h3>
        <div className="w-full overflow-x-auto rounded-xl border border-[#3F3F4C] bg-[#1A1A1E]">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-[#3F3F4C] bg-[#121214]">
                {["Lega", "Codice", "Creatore", "Membri", "Limite", "Azioni"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leagues.map((l, i) => (
                <tr key={l.id} data-testid={`admin-league-row-${l.code}`} className={`border-b border-[#242429] ${i % 2 === 0 ? "bg-[#1A1A1E]" : "bg-[#141416]"}`}>
                  <td className="px-4 py-2.5 font-body-sport font-semibold text-white text-sm">{l.name}</td>
                  <td className="px-4 py-2.5 font-time text-xs text-[#FFB703]">{l.code}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-400">{l.owner_username}</td>
                  <td className="px-4 py-2.5 font-time text-xs text-gray-300">{l.members_count}</td>
                  <td className="px-4 py-2.5 font-time text-xs text-gray-300">{l.max_members ?? 20}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1.5">
                      <button
                        data-testid={`league-rename-${l.id}`}
                        onClick={() => renameLeague(l)}
                        title="Rinomina lega"
                        className="p-1.5 rounded border border-[#3F3F4C] text-gray-400 hover:text-white hover:border-white transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`league-limit-${l.id}`}
                        onClick={() => limitLeague(l)}
                        title="Modifica limite partecipanti"
                        className="p-1.5 rounded border border-[#3F3F4C] text-gray-400 hover:text-[#FFB703] hover:border-[#FFB703] transition-colors"
                      >
                        <Users className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`league-delete-${l.id}`}
                        onClick={() => deleteLeague(l)}
                        title="Elimina lega"
                        className="p-1.5 rounded border border-[#3F3F4C] text-gray-400 hover:text-[#FF1E27] hover:border-[#FF1E27] transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {leagues.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-gray-500 text-sm py-8">
                    Nessuna lega privata creata.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
